/**
 * Status Configuration and Utilities
 *
 * SINGLE SOURCE OF TRUTH for all status configurations in the correspondence module.
 * Consolidates status configs previously duplicated in:
 * - inbox/constants.ts
 * - header/constants.ts
 * - CorrespondenceHistoryCard.tsx
 */

import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid';
import React from 'react';

import type { RenderStatus, TransmissionStatus } from '../types';

// ============================================================================
// Status Configuration Types
// ============================================================================

export interface StatusConfig {
  label: string;
  description: string;
  color: string;
  bgColor: string;
  dotColor: string;
  /** Combined className for simple badge usage */
  className: string;
  /** Dot className for StatusIndicator */
  dotClassName: string;
  animate?: boolean;
}

// ============================================================================
// Render Status Configuration
// ============================================================================

/** Render status configuration (draft, generating, ready, etc.) */
export const RENDER_STATUS_CONFIG: Record<RenderStatus, StatusConfig> = {
  draft: {
    label: 'Draft',
    description: 'Being prepared',
    color: 'text-stone-600',
    bgColor: 'bg-stone-100',
    dotColor: 'bg-stone-400',
    className: 'text-stone-500',
    dotClassName: 'bg-stone-400',
  },
  queued_for_render: {
    label: 'Queued',
    description: 'Waiting to generate',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    dotColor: 'bg-violet-500',
    className: 'text-violet-600',
    dotClassName: 'bg-violet-500',
    animate: true,
  },
  generating: {
    label: 'Generating',
    description: 'PDF is being generated',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    dotColor: 'bg-blue-500',
    className: 'text-blue-600',
    dotClassName: 'bg-blue-500',
    animate: true,
  },
  ready: {
    label: 'Ready to Send',
    description: 'Ready to send',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    dotColor: 'bg-emerald-500',
    className: 'text-emerald-600',
    dotClassName: 'bg-emerald-500',
  },
  missing_fields: {
    label: 'Missing Fields',
    description: 'Some fields need to be filled',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    dotColor: 'bg-amber-500',
    className: 'text-amber-600',
    dotClassName: 'bg-amber-500',
  },
  error: {
    label: 'Error',
    description: 'An error occurred',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    dotColor: 'bg-red-500',
    className: 'text-red-600',
    dotClassName: 'bg-red-500',
  },
  published: {
    label: 'Published',
    description: 'Package has been finalized and sent',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    dotColor: 'bg-indigo-500',
    className: 'text-indigo-600',
    dotClassName: 'bg-indigo-500',
  },
};

// ============================================================================
// Transmission Status Configuration
// ============================================================================

/** Transmission status configuration (pending, sending, sent, etc.) */
export const TRANSMISSION_STATUS_CONFIG: Record<
  TransmissionStatus,
  StatusConfig
> = {
  pending: {
    label: 'Queued',
    description: 'Waiting to be sent',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    dotColor: 'bg-yellow-500',
    className: 'text-yellow-600',
    dotClassName: 'bg-yellow-500',
    animate: true,
  },
  sending: {
    label: 'Sending',
    description: 'Transmission in progress',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    dotColor: 'bg-blue-500',
    className: 'text-blue-600',
    dotClassName: 'bg-blue-500',
    animate: true,
  },
  sent: {
    label: 'Sent',
    description: 'Successfully transmitted',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    dotColor: 'bg-indigo-500',
    className: 'text-indigo-600',
    dotClassName: 'bg-indigo-500',
  },
  delivered: {
    label: 'Delivered',
    description: 'Confirmed delivered',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    dotColor: 'bg-emerald-500',
    className: 'text-emerald-600',
    dotClassName: 'bg-emerald-500',
  },
  failed: {
    label: 'Failed',
    description: 'Transmission failed',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    dotColor: 'bg-red-500',
    className: 'text-red-600',
    dotClassName: 'bg-red-500',
  },
  bounced: {
    label: 'Bounced',
    description: 'Message was rejected',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    dotColor: 'bg-red-500',
    className: 'text-red-600',
    dotClassName: 'bg-red-500',
  },
};

// ============================================================================
// Combined Status Display (for components that handle both status types)
// ============================================================================

/** Combined status display config for components that handle both render and transmission status */
export const STATUS_DISPLAY: Record<string, StatusConfig> = {
  ...RENDER_STATUS_CONFIG,
  ...TRANSMISSION_STATUS_CONFIG,
};

// ============================================================================
// Validation Variant Styles (for ValidationBanner and ReadySummary)
// ============================================================================

export type ValidationVariant = 'ready' | 'blocked' | 'needs_action';

export interface ValidationVariantConfig {
  bg: string;
  icon: string;
  text: string;
  subtext: string;
}

/** Shared styling for validation readiness indicators */
export const VALIDATION_VARIANT_STYLES: Record<
  ValidationVariant,
  ValidationVariantConfig
> = {
  ready: {
    bg: 'bg-emerald-50/40',
    icon: 'text-emerald-500',
    text: 'text-emerald-800',
    subtext: 'text-emerald-600/80',
  },
  needs_action: {
    bg: 'bg-amber-50/40',
    icon: 'text-amber-500',
    text: 'text-amber-800',
    subtext: 'text-amber-600/80',
  },
  blocked: {
    bg: 'bg-amber-50/40',
    icon: 'text-amber-500',
    text: 'text-amber-800',
    subtext: 'text-amber-600/80',
  },
};

// ============================================================================
// Status Icons
// ============================================================================

/** Loading spinner for generating status */
const StatusSpinner: React.FC<{ size?: 'sm' | 'md' }> = ({ size = 'sm' }) => {
  const sizeClasses = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  return (
    <div
      className={`${sizeClasses} animate-spin rounded-full border-2 border-blue-200 border-t-blue-600`}
    />
  );
};

interface StatusIconProps {
  status: RenderStatus | string;
  size?: 'sm' | 'md';
}

/** Get status icon component for render status */
export const RenderStatusIcon: React.FC<StatusIconProps> = ({
  status,
  size = 'sm',
}) => {
  const sizeClasses = size === 'sm' ? 'h-3.5 w-3.5' : 'h-5 w-5';

  switch (status) {
    case 'ready':
      return <CheckCircleIcon className={`${sizeClasses} text-green-500`} />;
    case 'missing_fields':
      return (
        <ExclamationTriangleIcon className={`${sizeClasses} text-amber-500`} />
      );
    case 'error':
      return (
        <ExclamationCircleIcon className={`${sizeClasses} text-red-500`} />
      );
    case 'queued_for_render':
    case 'generating':
      return <StatusSpinner size={size} />;
    default:
      return null;
  }
};
